import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TableComponent } from '@ohpen/enso-ui';

import { hasListUrlState, isListOnScreen, LIST_URL_SPECS, readListUrlState, writeListUrlState } from '@pmp/utils';

/** Table props that keep the list's search text in the query string. */
export interface ListUrlStateProps {
  onGlobalSearch?: (searchText: string) => void;
}

/**
 * Two-way sync between the query string and one list's search box, so a link to a searched list
 * reproduces it: `search-<tableId>` is enso's own param, which the Table applies when it initialises and
 * which PMP already links between lists with. This adds the write-back.
 *
 * Column filters are deliberately not carried in the URL. They live in the grid, which persists them
 * itself, and the agent applies them through the table's own API; a filtered view is shared as a preset,
 * not as a link. Arriving on a link does clear them, see below.
 *
 * Applying the URL does not persist, so opening someone else's link does not become the recipient's
 * remembered state. A list with no entry in LIST_URL_SPECS gets no props at all and behaves as before.
 */
export const useListUrlState = (entityType: string | undefined, tableRef: RefObject<TableComponent | null>): ListUrlStateProps => {
  const [searchParams, setSearchParams] = useSearchParams();
  const registered = entityType === undefined ? undefined : LIST_URL_SPECS[entityType];

  // Only the first pass is an arrival. Typing in the search box writes the param back, which runs the
  // effect again, and that must not be mistaken for opening a link.
  const arrived = useRef(true);

  // A list whose tab is not open shares its URL with the list that is, so it neither applies what the
  // query string says nor writes its own state into it: that is the other list's state right now.
  const spec = registered !== undefined && isListOnScreen(searchParams, registered) ? registered : undefined;

  useEffect(() => {
    const table = tableRef.current;
    if (spec === undefined || table === null) return;

    const state = table.getState();
    const isArrival = arrived.current;
    arrived.current = false;

    // Nothing in the URL means the view restored from storage stands. Writing an empty state over it
    // would clear it, so the restored search is serialized instead and what is on screen stays
    // copy-pasteable. Only when it changes something, or the write feeds itself.
    if (!hasListUrlState(searchParams, spec)) {
      const restored = { search: state.search.text ?? '' };
      if (writeListUrlState(searchParams, spec, restored).toString() !== searchParams.toString()) {
        setSearchParams(previous => writeListUrlState(previous, spec, restored), { replace: true });
      }
      return;
    }

    const target = readListUrlState(searchParams, spec);

    // A link is how a change request gets shared, so opening one has to show what the sender saw. The
    // grid's column filters outlast the visit, so a filter left on this list intersects with the
    // search and hides the very row the link points at: the list answers "No data" and the link looks
    // broken. Cleared unasked rather than only when the grid reports filters, because it restores them
    // from storage after this runs and reports none at this point.
    if (isArrival) table.resetFilters();

    // `commit: false`, so applying the URL does not report a search the user never typed.
    if ((state.search.text ?? '') !== target.search) table.setSearch(target.search, { commit: false });
  }, [searchParams, setSearchParams, spec, tableRef]);

  // A search replaces rather than pushes: the back button should leave the page, not unwind every keystroke.
  const onGlobalSearch = useCallback(
    (searchText: string) => {
      if (spec === undefined) return;

      setSearchParams(previous => writeListUrlState(previous, spec, { search: searchText }), { replace: true });
    },
    [setSearchParams, spec],
  );

  return useMemo(() => (spec === undefined ? {} : { onGlobalSearch }), [onGlobalSearch, spec]);
};
